<script setup>
// Related-stories block (Figma node 75-3752). A dark-green rounded band with a
// two-column header, a responsive grid of story cards, and a centred pill CTA.
// Story cards reuse AcademyCard (name / quote / image / colour / cta); colour
// and image position alternate for rhythm. Rendered as a rounded panel (not a
// viewport full-bleed) to match the hero band's inset treatment and avoid
// horizontal-scroll overflow inside the capped content column.
import AcademyCard from '@/components/AcademyCard.vue'
import { withTrailingSlash } from '@/utils/siteUrl'

defineProps({
  block: { type: Object, required: true },
  // { heading?, intro?, stories: [{ name, quote, image{src,alt}, cta{label,to} }], cta?{label,to} }
})

const colourFor = (i) => (i % 2 === 0 ? 'lime' : 'cream')
const positionFor = (i) => (i % 2 === 0 ? 'bottom' : 'top')
</script>

<template>
  <section class="related-stories">
    <header v-if="block.heading || block.intro" class="head">
      <h2 v-if="block.heading" class="heading">{{ block.heading }}</h2>
      <p v-if="block.intro" class="intro">{{ block.intro }}</p>
    </header>

    <ul class="cards">
      <li v-for="(story, i) in block.stories" :key="i">
        <AcademyCard
          :name="story.name"
          :quote="story.quote"
          :image="story.image"
          :cta="story.cta || { label: 'Read the story', to: story.to || '#' }"
          :colour="colourFor(i)"
          :image-position="positionFor(i)"
        />
      </li>
    </ul>

    <div v-if="block.cta" class="cta-row">
      <RouterLink class="btn primary pill" :to="withTrailingSlash(block.cta.to)">
        {{ block.cta.label }} <span aria-hidden="true">→</span>
      </RouterLink>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.related-stories {
  background: var(--color-stories-band);
  @include rounded($radius-lg);
  color: var(--color-on-banner);
  padding: $space-8 $space-6;

  @include from-md { padding: $space-8; }

  .head {
    display: flex;
    flex-direction: column;
    gap: $space-4;
    margin-bottom: $space-8;

    @include from-md {
      flex-direction: row;
      align-items: flex-start;
      gap: $space-8;
      .heading { flex: 0 0 40%; }
      .intro   { flex: 1; }
    }

    .heading {
      margin: 0;
      font-size: $font-size-h2;
      line-height: $lh-heading;
      letter-spacing: $ls-heading;
      font-weight: 500;
    }

    .intro {
      margin: 0;
      font-size: $font-size-h4;
      line-height: 1.35;
    }
  }

  .cards {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-6;

    @include from-md { grid-template-columns: repeat(2, 1fr); }
    @include from-lg { grid-template-columns: repeat(3, 1fr); }
  }

  .cta-row {
    margin-top: $space-8;
    display: flex;
    justify-content: center;
  }

  // Pill CTA — same blue pill as the home Academy band / rich-text button.
  .pill {
    border-radius: $radius-pill;
    color: var(--color-bg);
    &:hover:not(:disabled) { color: var(--color-bg); }
  }
}
</style>
